import { Role, DbRef } from '@caresend/types';
import { objectFilter, errorSuffix, objectMap, decryptDraftWaypoints } from '@caresend/utils';
import { ref, onValue } from 'firebase/database';
import cloneDeep from 'lodash.clonedeep';
import { watch } from 'vue';
import 'firebase/auth';
import '../../../database/firebaseConfig/cloudFunctions.js';
import { firebaseDb } from '../../../database/firebaseConfig/init.js';
import '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { getRoute, maybeGetRoute } from '../../../router/helpers/initRouter.js';
import { getStore } from '../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import 'immutability-helper';
import { getMatchedMeta } from '../../../router/helpers/router.js';
import { DevRouteName } from '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import '../../../router/routes/dashboard/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/visitsPage/routes.js';
import { isProUser } from '../../helpers.js';
import { logAppVersion, initFWRAndVars } from './helpers.js';
import { BrandTheme, ConsumingApp, LoadingReason, globalLoadingStates } from './model.js';
import { initExtendedModule } from '../helpers.js';

const initAppState = (app) => ({
    app,
    clientOnline: false,
    customGlobalLoadingMessage: null,
    isInitialLoading: true,
    showRouteLoading: true,
    storybookShowWrappers: true,
    theme: BrandTheme.CARESEND,
    routeLoading: true,
    navBarVisible: true,
    loadingIDs: {},
});
const appMutations = {
    'app/SET_CLIENT_ONLINE': (state, online) => {
        state.clientOnline = online;
        console.info('rtdb connection status:', online ? 'online' : 'offline');
    },
    'app/SET_CUSTOM_GLOBAL_LOADING_MESSAGE': (state, message) => {
        state.customGlobalLoadingMessage = message;
    },
    'app/SET_DISPLAY_ROUTE_LOADING': (state, loading) => {
        state.showRouteLoading = loading;
    },
    'app/SET_IS_INITIAL_LOADING': (state, isInitialLoading) => {
        state.isInitialLoading = isInitialLoading;
    },
    'app/SET_ROUTE_LOADING': (state, loading) => {
        state.routeLoading = loading;
    },
    'app/SET_STORYBOOK_SHOW_WRAPPERS': (state, showWrappers) => {
        state.storybookShowWrappers = showWrappers;
    },
    'app/SET_THEME': (state, theme) => {
        state.theme = theme;
    },
    'app/SET_NAV_BAR_VISIBLE': (state, navBarVisible) => {
        state.navBarVisible = navBarVisible;
    },
    'app/SET_LOADING_IDS': (state, loadingIDs) => {
        const mergedLoadingIDs = { ...state.loadingIDs, ...loadingIDs };
        state.loadingIDs = objectFilter(mergedLoadingIDs, (id) => id);
    },
};
const appActions = {
    'app/bindConnected': async ({ commit }) => new Promise((resolve) => {
        try {
            const connectedRef = ref(firebaseDb, '.info/connected');
            onValue(connectedRef, (snapshot) => {
                commit('app/SET_CLIENT_ONLINE', snapshot.val() === true);
                // First callback resolves promise, but callback will continue to fire
                // when client goes offline/online.
                resolve();
            });
        }
        catch (error) {
            toastErrorAndReport(`There was a problem loading the app. ${errorSuffix}`, error);
        }
    }),
    'app/initApp': async ({ dispatch, state }) => {
        logAppVersion();
        await dispatch('app/bindConnected');
        if (!state.clientOnline) {
            // Create an inner promise to ensure `app/initApp` does not resolve until
            // variables are bound and utils helpers have been initialized.
            await new Promise((resolve) => {
                const unwatch = watch(() => state.clientOnline, async (online) => {
                    if (online) {
                        unwatch();
                        await initFWRAndVars();
                        resolve();
                    }
                    else if (window.location.pathname.includes('/advanced')) {
                        // If user is trying to access advanced settings, always resolve,
                        // but continue to watch for client to come online to bind variables
                        // later.
                        resolve();
                    }
                }, { immediate: true });
            });
        }
        else {
            await initFWRAndVars();
        }
    },
    'app/updateRouteLoading': ({ commit, state }, loading) => {
        commit('app/SET_ROUTE_LOADING', loading);
        const route = getRoute().value;
        const { loadUntilTrue } = getMatchedMeta(route);
        if (loading) {
            // Ensure loading is displayed without delay if `loadUntilTrue` is defined
            // and the computed value is false to enable a seamless transition between
            // loading screens.
            if (route.path === '/'
                || (loadUntilTrue && !loadUntilTrue(route).value)) {
                commit('app/SET_DISPLAY_ROUTE_LOADING', true);
            }
        }
        else if (!loading && loadUntilTrue && !loadUntilTrue(route).value) {
            if (!state.routeLoading)
                commit('app/SET_DISPLAY_ROUTE_LOADING', true);
            const originalRouteName = route.name;
            // Watch `loadUntilTrue` for a seamless transition between loading screens
            const unwatch = watch(() => loadUntilTrue(route).value, (stopLoading) => {
                if (stopLoading) {
                    const currentRouteName = getRoute().value.name;
                    // A new route may have started loading in the meantime. Only stop
                    // loading if the route hasn't changed.
                    if (originalRouteName === currentRouteName) {
                        commit('app/SET_DISPLAY_ROUTE_LOADING', false);
                    }
                    unwatch();
                }
            });
        }
        else {
            commit('app/SET_DISPLAY_ROUTE_LOADING', false);
        }
    },
    'app/showRouteLoading': ({ commit, state }) => {
        if (state.routeLoading) {
            commit('app/SET_DISPLAY_ROUTE_LOADING', true);
        }
    },
};
const appGetters = {
    /** Get the browser title prefix for a given app */
    'app/getAppTitle': (state) => {
        switch (state.app) {
            case ConsumingApp.DASHBOARD:
                return 'CareSend Dashboard';
            case ConsumingApp.PATIENT:
                return 'CareSend';
            case ConsumingApp.PRO:
                return 'CareSend Pro';
        }
    },
    'app/getIsLoadingBlocking': (state, getters, rootState) => {
        const loadingState = appGetters['app/getLoadingState'](state, getters, rootState);
        return !!(loadingState === null || loadingState === void 0 ? void 0 : loadingState.blocking);
    },
    'app/getLoadingMessage': (state, getters, rootState) => {
        const loadingState = appGetters['app/getLoadingState'](state, getters, rootState);
        if (!loadingState)
            return null;
        if (loadingState.reason === LoadingReason.CUSTOM) {
            return loadingState.message || null;
        }
        return loadingState.reason;
    },
    'app/getLoadingState': (state, _getters, rootState) => {
        var _a;
        if (state.customGlobalLoadingMessage) {
            return {
                getIsLoading: () => true,
                message: state.customGlobalLoadingMessage,
                reason: LoadingReason.CUSTOM,
            };
        }
        const defaultGlobalLoadingState = globalLoadingStates
            .find((loadingState) => loadingState.getIsLoading({ rootState, state }));
        if (defaultGlobalLoadingState) {
            const { getCustomLoadingMessage } = defaultGlobalLoadingState;
            if (getCustomLoadingMessage) {
                const customMessage = getCustomLoadingMessage({
                    route: maybeGetRoute().value,
                });
                if (customMessage) {
                    return {
                        ...defaultGlobalLoadingState,
                        message: customMessage,
                        reason: LoadingReason.CUSTOM,
                    };
                }
            }
            const defaultBlockingSetting = defaultGlobalLoadingState.blocking;
            // Do not prevent devs from accessing `/advanced`
            const overrideBlocking = ((_a = maybeGetRoute().value) === null || _a === void 0 ? void 0 : _a.name) === DevRouteName.DEV_ADVANCED_SETTINGS;
            return {
                ...defaultGlobalLoadingState,
                blocking: overrideBlocking ? undefined : defaultBlockingSetting,
            };
        }
        return null;
    },
    /**
     * Returns null while data is loading. Menu info will not display until an
     * object is returned.
     */
    'app/getMenuInfo': (_state, _getters, rootState, rootGetters) => {
        var _a, _b, _c, _d, _e, _f;
        const { user } = rootState.auth;
        if (!user)
            return null;
        const baseInfo = { email: user.info.email };
        if (!isProUser())
            return baseInfo;
        switch (user.role) {
            case Role.ASSISTANT:
            case Role.PRESCRIBER: {
                const officeName = (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.info.name;
                if (!officeName)
                    return null;
                return {
                    ...baseInfo,
                    main: officeName,
                    secondary: user.info.email,
                };
            }
            case Role.PACKER: {
                const placeID = (_c = rootState.places) === null || _c === void 0 ? void 0 : _c.userAssociatedPlaceID;
                let placeGroupName;
                let placeAddress;
                if (placeID) {
                    const placeGroup = (_d = rootGetters['places/getPlaceGroupByPlaceID']) === null || _d === void 0 ? void 0 : _d.call(rootGetters, placeID);
                    placeGroupName = placeGroup === null || placeGroup === void 0 ? void 0 : placeGroup.name;
                    placeAddress = (_f = (_e = rootGetters['places/getPlaceAddress']) === null || _e === void 0 ? void 0 : _e.call(rootGetters, placeID)) !== null && _f !== void 0 ? _f : undefined;
                }
                return {
                    ...baseInfo,
                    main: placeGroupName,
                    secondary: placeAddress,
                };
            }
            default:
                return baseInfo;
        }
    },
    'app/getDefaultFetchWithRelationshipsCallbacks': (_s, _g) => (commit, dispatch) => ({
        [DbRef.WAYPOINTS]: (waypoints) => commit === null || commit === void 0 ? void 0 : commit('waypoint/SET_WAYPOINTS', waypoints),
        [DbRef.WAYPOINT_ACTIONS]: (waypointActions) => commit === null || commit === void 0 ? void 0 : commit('waypoint/SET_WAYPOINT_ACTIONS', waypointActions),
        [DbRef.PLACE_GROUPS]: (placeGroups) => commit === null || commit === void 0 ? void 0 : commit('places/SET_PLACE_GROUPS', placeGroups),
        [DbRef.PLACES]: (places) => commit === null || commit === void 0 ? void 0 : commit('places/SET_PLACES', places),
        [DbRef.PROCEDURES]: (procedures) => commit === null || commit === void 0 ? void 0 : commit('procedures/SET_PROCEDURES', procedures),
        [DbRef.BOOKINGS]: (bookings) => commit === null || commit === void 0 ? void 0 : commit('bookings/SET_BOOKINGS', bookings),
        [DbRef.ITINERARIES]: (itineraries) => commit === null || commit === void 0 ? void 0 : commit('waypoint/SET_ITINERARIES', itineraries),
        [DbRef.OFFICES]: (offices) => commit === null || commit === void 0 ? void 0 : commit('offices/SET_OFFICES', offices),
        [DbRef.ORDERS]: (orders) => commit === null || commit === void 0 ? void 0 : commit('orders/SET_ORDERS', orders),
        /** Special case for shipments, we need to decrypt them first */
        [DbRef.SHIPMENTS]: (shipments) => {
            if (dispatch) {
                commit === null || commit === void 0 ? void 0 : commit('app/SET_LOADING_IDS', objectMap(shipments, () => true));
                dispatch('supplies/decryptAndSetShipments', { shipments })
                    .finally(() => commit === null || commit === void 0 ? void 0 : commit('app/SET_LOADING_IDS', objectMap(shipments, () => false)));
            }
            else {
                commit === null || commit === void 0 ? void 0 : commit('supplies/SET_SHIPMENTS', shipments);
            }
        },
        [DbRef.SUPPLY_TRANSFERS]: (supplyTransfers) => commit === null || commit === void 0 ? void 0 : commit('supplies/SET_SUPPLY_TRANSFERS', supplyTransfers),
        [DbRef.DRAFT_WAYPOINTS]: async (draftWaypoints) => {
            try {
                const decryptedDraftWaypoints = await decryptDraftWaypoints(draftWaypoints);
                return commit === null || commit === void 0 ? void 0 : commit('waypoint/SET_DRAFT_WAYPOINTS', decryptedDraftWaypoints !== null && decryptedDraftWaypoints !== void 0 ? decryptedDraftWaypoints : {});
            }
            catch (error) {
                console.error('Error decrypting draft waypoints: ', error);
            }
        },
        [DbRef.SHIFTS]: (shifts) => commit === null || commit === void 0 ? void 0 : commit('shifts/SET_SHIFTS', shifts),
        [DbRef.USERS]: (users) => commit === null || commit === void 0 ? void 0 : commit('users/SET_USERS', Object.values(cloneDeep(users))),
        loadingIDs: (loadingIDs) => commit === null || commit === void 0 ? void 0 : commit('app/SET_LOADING_IDS', loadingIDs),
    }),
    'app/getIsLoading': (state) => (input) => !!state.loadingIDs[typeof input === 'string' ? input : input.id],
};
const initAppWatches = () => {
    const store = getStore();
    // The first time the loading state is changed to null, set `isInitialLoading`
    // to false.
    store.watch((_state, getters) => getters['app/getLoadingState'], async (loadingState) => {
        if (!loadingState && store.state.app.isInitialLoading) {
            store.commit('app/SET_IS_INITIAL_LOADING', false);
        }
    });
};
const getAppModule = (app) => ({
    state: initAppState(app),
    mutations: appMutations,
    actions: appActions,
    getters: appGetters,
});
const initAppModule = (app, moduleExtensions) => initExtendedModule(getAppModule(app), moduleExtensions);

export { initAppModule, initAppWatches };
